import { type FC, type RefObject, useState } from 'react';

import type { MultiSelectFilterModifier } from '@iconik-io/shared-lib/src/redux/common_filters/types';
import { selectionToFirstStringItem } from '@iconik-io/shared-lib/src/utils/selection_to_first_string_item';
import {
  Button,
  DropdownListBox,
  DropdownListBoxItem,
  DropdownListBoxItemText,
  Icon,
  Popover,
  type Selection,
} from '@iconik-io/uad';

import {
  getAvailableModifierOptions,
  getModifierDisplayLabel,
  normalizeModifier,
} from '../../../../utils/search_utils/modifier_options';

import styles from './multi_select_filter_modifier_dropdown.module.css';

type Props = {
  modifier: MultiSelectFilterModifier;
  itemCount: number;
  availableModifiers: MultiSelectFilterModifier[];
  onChange: (modifier: MultiSelectFilterModifier) => void;
  isDisabled?: boolean;
  triggerRef: RefObject<HTMLElement | null>;
};

export const MultiSelectFilterModifierDropdown: FC<Props> = ({
  modifier,
  itemCount,
  availableModifiers,
  onChange,
  isDisabled,
  triggerRef,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const options = getAvailableModifierOptions(availableModifiers, itemCount);
  // Normalize the selected key onto an offered option (single-item collapse, or
  // a clamped modifier set), so both the button label and the selection match.
  const normalizedModifier = normalizeModifier(
    modifier,
    availableModifiers,
    itemCount,
  );
  const displayLabel = getModifierDisplayLabel(normalizedModifier, itemCount);

  const handleSelectionChange = (selection: Selection) => {
    const selected = selectionToFirstStringItem(selection);
    const selectedModifier = availableModifiers.find(
      (item) => item === selected,
    );
    if (selectedModifier) {
      onChange(selectedModifier);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="tertiary"
        size="s"
        endIcon={<Icon name="chevron_small_isolated_s" size="s" />}
        className={styles.trigger}
        isDisabled={isDisabled}
        aria-label="Select modifier"
        onPress={() => setIsOpen(true)}
        data-open={isOpen || undefined}
      >
        {displayLabel}
      </Button>
      {isOpen && (
        <Popover
          isOpen
          triggerRef={triggerRef}
          onOpenChange={(open) => {
            if (!open) {
              setIsOpen(false);
            }
          }}
          className={styles.popover}
          placement="bottom start"
        >
          <DropdownListBox
            className={styles.listbox}
            items={options}
            selectionMode="single"
            selectedKeys={[normalizedModifier]}
            onSelectionChange={handleSelectionChange}
            autoFocus="first"
            shouldFocusOnHover
            disallowEmptySelection
            aria-label="Filter modifier"
            size="s"
          >
            {(option) => (
              <DropdownListBoxItem
                id={option.value}
                textValue={option.label}
                className={styles.item}
              >
                <DropdownListBoxItemText variant="value_s">
                  {option.label}
                </DropdownListBoxItemText>
              </DropdownListBoxItem>
            )}
          </DropdownListBox>
        </Popover>
      )}
    </>
  );
};
